import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import {
  AFTER_SIGNED_SCAN_REVIEW_INTRO,
  AFTER_SIGNED_SCAN_REVIEW_SCOPE_COPY,
  AFTER_SIGNED_SCAN_REVIEW_TITLE,
  REVIEW_FULL_SAFETY_CASE_HISTORY_LABEL,
  formatAfterSignedScanTimestamp,
  formatSignedScanScopeLabel,
  type AfterSignedScanReviewContext,
  type AfterSignedScanTimelineChange,
} from '../../domain/incidents/afterSignedScanReview';
import { RISK_PATTERN_REVIEW_PAGE_COPY } from '../../domain/incidents/riskPatternWorkflowReviewContext';
import { AfterSignedScanTimelineSelector } from './AfterSignedScanTimelineSelector';
import { formatObservationCount } from '../../domain/copy/countNoun';
import type { ReviewWindowContext } from '../../domain/incidents/reviewWindowContext';
import { formatObservationCountLine } from '../../domain/incidents/reviewWindowContext';

export type AfterSignedScanReviewPanelProps = {
  context: AfterSignedScanReviewContext;
  reviewWindowContext?: ReviewWindowContext | null;
  scopedActivityCount: number | null;
  latestObservation: string | null;
  reviewWindowEnd?: string | null;
  reviewState: string | null;
  linkedSafetyCaseTitle?: string | null;
  safetyCaseId?: string | null;
  originatingRiskPattern?: string | null;
  caseStateLabel?: string | null;
  onTimelineChange?: (change: AfterSignedScanTimelineChange) => void;
  onResolveStartBeforeSignedScan?: (action: 'full_history' | 'reset_start') => void;
  onReviewFullHistory?: () => void;
  onOpenSafetyCase?: () => void;
  onViewSignedSafetyScan?: () => void;
  onReviewNewActivity?: () => void;
  reviewNewActivityHref?: string | null;
  onCreateUpdatedSafetyScan?: () => void;
  siteTimeZone?: string | null;
};

function formatLinkedSafetyCaseLabel(
  linkedSafetyCaseTitle?: string | null,
  safetyCaseId?: string | null,
): string {
  const linkedTitle = String(linkedSafetyCaseTitle || '').trim();
  if (linkedTitle) {
    return linkedTitle.replace(/^Safety Case:\s*/i, '').trim() || linkedTitle;
  }
  return String(safetyCaseId || '').trim() || 'Not linked';
}

function formatNewActivityCountLabel(count: number, reviewWindowContext?: ReviewWindowContext | null): string {
  if (reviewWindowContext) {
    return formatObservationCountLine(count, reviewWindowContext);
  }
  return `${formatObservationCount(count)} in current new-activity window`;
}

export const AfterSignedScanReviewPanel: React.FC<AfterSignedScanReviewPanelProps> = ({
  context,
  reviewWindowContext,
  scopedActivityCount,
  latestObservation,
  reviewWindowEnd,
  reviewState,
  linkedSafetyCaseTitle,
  safetyCaseId,
  originatingRiskPattern,
  caseStateLabel,
  onTimelineChange,
  onResolveStartBeforeSignedScan,
  onReviewFullHistory,
  onOpenSafetyCase,
  onViewSignedSafetyScan,
  onReviewNewActivity,
  reviewNewActivityHref,
  onCreateUpdatedSafetyScan,
  siteTimeZone = null,
}) => {
  if (!context.active) return null;

  const activityCount = scopedActivityCount ?? context.newActivityCount;
  const observationIso = latestObservation || context.newActivityLatestObservation;
  const observationLabel = formatAfterSignedScanTimestamp(observationIso, siteTimeZone);
  const reviewStateLabel = String(
    caseStateLabel || reviewState || context.newActivityReviewState || '',
  ).trim() || 'Not recorded';
  const newActivityWindowStartLabel = formatAfterSignedScanTimestamp(
    context.scanScopeStart || context.newActivityWindowStart || context.signedScanAt,
    siteTimeZone,
  );
  const newActivityWindowEndLabel = (() => {
    const endIso = String(reviewWindowEnd || '').trim();
    if (endIso) return formatAfterSignedScanTimestamp(endIso, siteTimeZone);
    return 'now';
  })();
  const reviewNewActivityLabel = RISK_PATTERN_REVIEW_PAGE_COPY.state_c.actions.reviewNewActivity;

  return (
    <Alert
      severity="info"
      data-testid="after-signed-scan-review-panel"
      sx={{ mb: 2, borderRadius: 2 }}
    >
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {AFTER_SIGNED_SCAN_REVIEW_TITLE}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {AFTER_SIGNED_SCAN_REVIEW_INTRO}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {AFTER_SIGNED_SCAN_REVIEW_SCOPE_COPY}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Creating an Updated Safety Scan generates a new report package; the previously Signed
            Safety Scan remains unchanged.
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Show
          </Typography>
          <Typography variant="body2">
            {`Linked Safety Case: ${formatLinkedSafetyCaseLabel(linkedSafetyCaseTitle, safetyCaseId)}`}
          </Typography>
          <Typography variant="body2">
            {`Originating Risk Pattern: ${String(originatingRiskPattern || '').trim() || 'Not recorded'}`}
          </Typography>
          <Typography variant="body2">
            {`Current case state: ${reviewStateLabel}`}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Latest Signed Safety Scan
          </Typography>
          <Typography variant="body2">
            {formatAfterSignedScanTimestamp(context.signedScanAt, siteTimeZone)}
          </Typography>
          <Typography variant="body2">
            {`Latest Signed Safety Scan scope: ${formatSignedScanScopeLabel(
              context.signedScanScopeStart,
              context.signedScanScopeEnd,
              siteTimeZone,
            )}`}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            New activity window
          </Typography>
          {onTimelineChange && context.signedScanAt ? (
            <Box sx={{ mt: 1 }}>
              <AfterSignedScanTimelineSelector
                signedScanAt={context.signedScanAt}
                timelineMode={context.timelineMode}
                windowStartIso={context.scanScopeStart || context.newActivityWindowStart || context.signedScanAt}
                windowEndIso={context.scanScopeEnd || reviewWindowEnd}
                onTimelineChange={onTimelineChange}
                onResolveStartBeforeSignedScan={onResolveStartBeforeSignedScan || (() => undefined)}
                siteTimeZone={siteTimeZone}
              />
            </Box>
          ) : null}
          <Typography variant="body2" sx={{ mt: onTimelineChange ? 1 : 0 }}>
            {`Start: ${newActivityWindowStartLabel}`}
          </Typography>
          <Typography variant="body2">
            {`End: ${newActivityWindowEndLabel}`}
          </Typography>
          <Typography variant="body2">
            {`Latest observation: ${observationLabel}`}
          </Typography>
          {activityCount != null ? (
            <Box data-testid="pattern-new-activity" sx={{ mt: 1 }}>
              <Typography
                sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}
              >
                NEW ACTIVITY
              </Typography>
              <Typography variant="body2">
                {formatNewActivityCountLabel(activityCount, reviewWindowContext)}
              </Typography>
            </Box>
          ) : null}
        </Box>

        {onOpenSafetyCase || onViewSignedSafetyScan || onReviewNewActivity || onCreateUpdatedSafetyScan ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {onOpenSafetyCase ? (
              <Button size="small" variant="contained" onClick={onOpenSafetyCase}>
                Open Safety Case
              </Button>
            ) : null}
            {onReviewNewActivity ? (
              <Button
                size="small"
                variant="text"
                href={reviewNewActivityHref || undefined}
                onClick={(event) => {
                  if (reviewNewActivityHref) event.preventDefault();
                  onReviewNewActivity();
                }}
              >
                {reviewNewActivityLabel}
              </Button>
            ) : null}
            {onViewSignedSafetyScan ? (
              <Button size="small" variant="outlined" onClick={onViewSignedSafetyScan}>
                View Signed Safety Scan
              </Button>
            ) : null}
            {onCreateUpdatedSafetyScan ? (
              <Button size="small" variant="outlined" onClick={onCreateUpdatedSafetyScan}>
                Create Updated Safety Scan
              </Button>
            ) : null}
          </Stack>
        ) : null}

        {onReviewFullHistory ? (
          <Button
            size="small"
            variant="text"
            onClick={onReviewFullHistory}
            sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, textTransform: 'none' }}
          >
            {REVIEW_FULL_SAFETY_CASE_HISTORY_LABEL}
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
};
